import React, { FC, useCallback } from "react";
import { useIntl } from "react-intl";
import { GridFilterModel, GridSortItem } from "@mui/x-data-grid-pro";
import { useAppContext } from "../../Unknown/AppContext";
import useRows, { normalizeAppointment } from "./useRows";
import AppointmentsTableGrid from "../AppointmentTableGrid";
import { DashboardAppointment } from "../AppointmentTableContainer/getAppointments";
import { DateRangePickerProps } from "../../Unknown/DateRangePicker";
import useAppointmentCustomerLogInDialog from "../AppointmentCardsContainer/useAppointmentCustomerLogInDialog";
import useAppointmentCheckInDialog from "../AppointmentCardsContainer/useAppointmentCheckInDialog";
import useSystemConfig from "../../../hooks/useSystemConfig";
import { AppointmentRowItem } from "../AppointmentTableGrid/types";

type AppointmentTableProps = {
  role: string | undefined;
  appointments: DashboardAppointment[];
  isLoading: boolean;
  isFetching?: boolean;
  dateRangePickerProps?: DateRangePickerProps<Date>;
  refetch: () => void;
  filterModel: GridFilterModel;
  sortModel: GridSortItem[];
  paginationModel: { page: number; pageSize: number };
  onFilterModelChange: (model: GridFilterModel) => void;
  onSortModelChange: (model: GridSortItem[]) => void;
  onPaginationModelChange: (model: { page: number; pageSize: number }) => void;
  rowCount: number;
  fetchAllForExport?: () => Promise<DashboardAppointment[]>;
};

const adminStatuses = [
  "incomplete",
  "confirmed",
  "checked_in",
  "abandoned",
  "checkout",
  "intake_complete",
  "no_show",
  "canceled",
  "studies_unread",
  "studies_read",
  "reports_sent",
  "reports_error",
];

const AppointmentTable: FC<AppointmentTableProps> = ({
  role,
  appointments,
  isLoading,
  isFetching,
  dateRangePickerProps,
  refetch,
  filterModel,
  sortModel,
  paginationModel,
  onFilterModelChange,
  onSortModelChange,
  onPaginationModelChange,
  rowCount,
  fetchAllForExport,
}) => {
  const {
    databaseUserData: { centerIds },
  } = useAppContext();

  const { props: loginDialogProps, handleOpen: handleOpenLoginDialog } =
    useAppointmentCustomerLogInDialog();

  const { props: checkInDialogProps, handleOpen: handleOpenCheckInDialog } =
    useAppointmentCheckInDialog({ onSuccess: refetch });

  const rows = useRows({
    appointments,
    handleOpenLoginDialog,
    handleOpenCheckInDialog,
  });

  const { dataState } = useSystemConfig();
  const intl = useIntl();

  const fetchAllRowsForExport = useCallback(async (): Promise<
    AppointmentRowItem[]
  > => {
    if (!fetchAllForExport || dataState.status !== "success") return [];
    const appts = await fetchAllForExport();
    return appts.map((appointment) =>
      normalizeAppointment({
        appointment,
        systemConfig: dataState.data,
        intl,
        handleOpenLoginDialog: () => {},
        handleOpenCheckInDialog: () => {},
      }),
    );
  }, [fetchAllForExport, dataState, intl]);

  const localeFormat = centerIds.length === 1 ? "center" : "client";

  switch (role) {
    case "regional_admin":
    case "super_admin":
      return (
        <AppointmentsTableGrid
          columnsConfig={{
            columnItems: [
              { field: "flags" },
              { field: "status", valueOptions: adminStatuses },
              { field: "date", localeFormat },
              { field: "time", localeFormat },
              { field: "location" },
              { field: "locationState" },
              { field: "locationZip" },
              { field: "patientName" },
              { field: "patientAge" },
              { field: "patientDOB" },
              { field: "patientPhone" },
              { field: "patientEmail" },
              { field: "patientAddressLine1" },
              { field: "patientAddressLine2" },
              { field: "patientCity" },
              { field: "patientZip" },
              { field: "patientState" },
              { field: "patientId" },
              { field: "patientRace" },
              { field: "patientEthnicity" },
              { field: "patientSex" },
              { field: "centerServices" },
              { field: "addOns" },
              { field: "appointmentTotal" },
              { field: "bookingScenario" },
              { field: "bookedBy" },
              { field: "bookedDate", localeFormat },
              { field: "bookedTime", localeFormat },
              { field: "customer" },
              { field: "payments" },
              { field: "coupon" },
              { field: "isPast" },
            ],
          }}
          rows={rows}
          loginDialogProps={loginDialogProps}
          checkInDialogProps={checkInDialogProps}
          isLoading={isLoading}
          isFetching={isFetching}
          dateRangePickerProps={dateRangePickerProps}
          fetchAllRowsForExport={fetchAllRowsForExport}
          initialColumnVisibilityModel={{
            flags: false,
            bookingScenario: false,
            bookedBy: false,
            bookedDate: false,
            bookedTime: false,
            createdAt: false,
            locationState: false,
            locationZip: false,
            addOns: false,
            patientAddress: false,
            customer: false,
            payments: false,
            patientState: false,
            patientZip: false,
            isPast: false,
            patientAddressLine1: false,
            patientAddressLine2: false,
            patientCity: false,
            patientId: false,
            patientRace: false,
            patientEthnicity: false,
            patientSex: false,
            appointmentTotal: false,
          }}
          filterModel={filterModel}
          sortModel={sortModel}
          paginationModel={paginationModel}
          onFilterModelChange={onFilterModelChange}
          onSortModelChange={onSortModelChange}
          onPaginationModelChange={onPaginationModelChange}
          rowCount={rowCount}
        />
      );
    default:
      return null;
  }
};

export default AppointmentTable;
